import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronRight, Activity } from "lucide-react";
import { fetchStudyHeatmap } from "../services/dashboardService";

const BASE_TREEMAP_PALETTE = [
  "#16a34a", // 1. >= 70%: Green
  "#22c55e", // 2. 50% - 69%: Soft Green
  "#4a7c59", // 3. 30% - 49%: Sage / Olive Green-Grey
  "#64748b", // 4. 10% - 29%: Slate Grey
  "#475569", // 5. < 10%: Neutral Grey
];

const getTreemapColor = (completionPercent) => {
  const comp = Number(completionPercent) || 0;
  if (comp >= 70) return BASE_TREEMAP_PALETTE[0];
  if (comp >= 50) return BASE_TREEMAP_PALETTE[1];
  if (comp >= 30) return BASE_TREEMAP_PALETTE[2];
  if (comp >= 10) return BASE_TREEMAP_PALETTE[3];
  return BASE_TREEMAP_PALETTE[4];
};

export default function SyllabusHeatmap({
  activeSyllabusTab = "All",
  syllabusData = [],
  searchQuery = "",
}) {
  const [studyHeatmapData, setStudyHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedHeatmapSubject, setSelectedHeatmapSubject] = useState(null);
  const treemapContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        setLoading(true);
        const res = await fetchStudyHeatmap();
        if (isMounted && res && res.success && Array.isArray(res.seriesData)) {
          setStudyHeatmapData(res.seriesData);
        }
      } catch (err) {
        console.warn("Could not fetch study heatmap in syllabus:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Reset drilldown when switching tabs (e.g. from Core to Strategic)
  useEffect(() => {
    setSelectedHeatmapSubject(null);
  }, [activeSyllabusTab]);

  // Determine allowed subject names based on activeSyllabusTab (Core, Strategic, or All)
  const allowedSubjectNames = useMemo(() => {
    if (!syllabusData || syllabusData.length === 0 || activeSyllabusTab === "All") {
      return null;
    }
    const filtered = syllabusData.filter((s) => s.sub_type === activeSyllabusTab);
    return new Set(filtered.map((s) => s.subject_name.toLowerCase()));
  }, [syllabusData, activeSyllabusTab]);

  // Filter studyHeatmapData according to allowed subjects & search query
  const filteredHeatmapData = useMemo(() => {
    if (!studyHeatmapData || !Array.isArray(studyHeatmapData)) return [];

    let data = studyHeatmapData;

    // Filter by Core/Strategic if applicable
    if (allowedSubjectNames) {
      const matchingSubjectIds = new Set();
      data.forEach((item) => {
        if (!item.parent && allowedSubjectNames.has(item.name.toLowerCase())) {
          matchingSubjectIds.add(item.id);
        }
      });
      data = data.filter(
        (item) =>
          (!item.parent && matchingSubjectIds.has(item.id)) ||
          (item.parent && matchingSubjectIds.has(item.parent))
      );
    }

    // Filter by search query if user typed search
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const matchingSubjectIds = new Set();
      data.forEach((item) => {
        if (item.name.toLowerCase().includes(q)) {
          if (!item.parent) {
            matchingSubjectIds.add(item.id);
          } else {
            matchingSubjectIds.add(item.parent);
          }
        }
      });
      data = data.filter(
        (item) =>
          (!item.parent && matchingSubjectIds.has(item.id)) ||
          (item.parent && matchingSubjectIds.has(item.parent))
      );
    }

    return data;
  }, [studyHeatmapData, allowedSubjectNames, searchQuery]);

  // Treemap dataset calculation (Level 1: Subjects, Level 2: Topics under chosen Subject)
  const treemapData = useMemo(() => {
    if (filteredHeatmapData && filteredHeatmapData.length > 0) {
      if (!selectedHeatmapSubject) {
        // Level 1: ONLY Subjects
        const subjectItems = filteredHeatmapData.filter((item) => !item.parent);
        const totalSubjectHours = subjectItems.reduce(
          (sum, item) => sum + (item.completed_hours || item.value || 0),
          0
        );

        return subjectItems.map((item) => {
          const comp = item.completion_percent || 0;
          const color = getTreemapColor(comp);
          const hours = item.completed_hours || item.value || 0;
          return {
            id: item.id,
            name: item.name,
            value: hours > 0 ? hours : 1,
            color: color,
            hours: hours,
            percent: totalSubjectHours > 0 ? Math.round((hours / totalSubjectHours) * 100) : 0,
            completionPercent: comp,
            completedSubtopics: item.completed_subtopics || 0,
            totalSubtopics: item.total_subtopics || 0,
            isSubjectLevel: true,
          };
        });
      } else {
        // Level 2: Topics under the chosen subject
        const subjectNode = filteredHeatmapData.find(
          (item) => !item.parent && item.name.toLowerCase() === selectedHeatmapSubject.toLowerCase()
        );
        const subjectId = subjectNode ? subjectNode.id : null;

        // Strictly match by exact parent ID (e.g. parent === "subj_1")
        const topicItems = filteredHeatmapData.filter(
          (item) => item.parent && subjectId && item.parent === subjectId
        );

        if (topicItems.length > 0) {
          return topicItems.map((item) => {
            const comp = item.completion_percent || 0;
            const color = getTreemapColor(comp);
            const hours = item.completed_hours || item.value || 0;
            return {
              id: item.id,
              name: item.name,
              value: hours > 0 ? hours : 0.5,
              color: color,
              hours: hours,
              completionPercent: comp,
              completedSubtopics: item.completed_subtopics || 0,
              totalSubtopics: item.total_subtopics || 0,
              isSubjectLevel: false,
            };
          });
        }

        // If no topic items found in heatmap series for this subject, look up syllabusData
        if (syllabusData && syllabusData.length > 0) {
          const found = syllabusData.find(
            (s) => s.subject_name.toLowerCase() === selectedHeatmapSubject.toLowerCase()
          );
          if (found && found.topics && found.topics.length > 0) {
            return found.topics.map((t) => {
              const comp = t.completion_percent || 0;
              const hours = t.estd_hours || 0;
              return {
                id: `topic_${t.id || t.topic_id}`,
                name: t.name || t.topic_name,
                value: hours > 0 ? hours : 0.5,
                color: getTreemapColor(comp),
                hours: hours,
                completionPercent: comp,
                isSubjectLevel: false,
              };
            });
          }
        }
      }
    }

    // Fallback: If syllabusData is available from syllabus aggregate, create nodes
    if (syllabusData && syllabusData.length > 0) {
      const filtered = allowedSubjectNames
        ? syllabusData.filter((s) => allowedSubjectNames.has(s.subject_name.toLowerCase()))
        : syllabusData;

      if (!selectedHeatmapSubject) {
        return filtered.map((s) => ({
          id: `subj_${s.id || s.subject_id}`,
          name: s.subject_name,
          value: Math.max(1, s.topics ? s.topics.length : 1),
          color: getTreemapColor(s.completion_percent || 0),
          hours: s.completion_hours || 0,
          completionPercent: s.completion_percent || 0,
          totalSubtopics: s.total_subtopics || 0,
          completedSubtopics: s.completed_subtopics || 0,
          isSubjectLevel: true,
        }));
      } else {
        const found = filtered.find(
          (s) => s.subject_name.toLowerCase() === selectedHeatmapSubject.toLowerCase()
        );
        if (found && found.topics && found.topics.length > 0) {
          return found.topics.map((t) => ({
            id: `topic_${t.id || t.topic_id}`,
            name: t.name || t.topic_name,
            value: Math.max(0.5, t.estd_hours || 1),
            color: getTreemapColor(t.completion_percent || 0),
            hours: t.estd_hours || 0,
            completionPercent: t.completion_percent || 0,
            isSubjectLevel: false,
          }));
        }
      }
    }

    return [];
  }, [filteredHeatmapData, selectedHeatmapSubject, syllabusData, allowedSubjectNames]);

  // Overall summary stats
  const heatmapSummary = useMemo(() => {
    if (filteredHeatmapData && filteredHeatmapData.length > 0) {
      if (selectedHeatmapSubject) {
        const subjectNode = filteredHeatmapData.find(
          (item) => !item.parent && item.name.toLowerCase() === selectedHeatmapSubject.toLowerCase()
        );
        const hours = subjectNode ? Math.round((subjectNode.completed_hours || subjectNode.value || 0) * 10) / 10 : 0;
        const sessions = subjectNode ? subjectNode.completed_subtopics || 0 : 0;
        return { hours, sessions };
      }

      const subjectItems = filteredHeatmapData.filter((item) => !item.parent);
      const totalHours = Math.round(
        subjectItems.reduce((acc, curr) => acc + (curr.completed_hours || curr.value || 0), 0) * 10
      ) / 10;
      const totalCompletedSubtopics = subjectItems.reduce(
        (acc, curr) => acc + (curr.completed_subtopics || 0),
        0
      );
      return {
        hours: totalHours,
        sessions: totalCompletedSubtopics,
      };
    }
    return { hours: 0, sessions: 0 };
  }, [filteredHeatmapData, selectedHeatmapSubject]);

  // Initialize and render Highcharts Treemap
  useEffect(() => {
    let isMounted = true;
    let pollInterval = null;

    const renderChart = () => {
      if (!isMounted || !treemapContainerRef.current) return;
      if (!window.Highcharts || !window.Highcharts.seriesTypes || !window.Highcharts.seriesTypes.treemap) {
        return;
      }
      if (treemapData.length === 0) return;

      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy();
        } catch (_) {}
      }

      const calculatedHeight = selectedHeatmapSubject ? 380 : 340;

      try {
        chartInstanceRef.current = window.Highcharts.chart(treemapContainerRef.current, {
          chart: {
            type: "treemap",
            backgroundColor: "transparent",
            height: calculatedHeight,
            spacing: [2, 2, 2, 2],
            margin: [2, 2, 2, 2],
            animation: { duration: 250 },
          },
          title: { text: null },
          credits: { enabled: false },
          accessibility: { enabled: false },
          legend: { enabled: false },
          tooltip: {
            useHTML: true,
            backgroundColor: "#0f172a",
            borderColor: "#1e293b",
            borderRadius: 8,
            padding: 10,
            shadow: true,
            style: {
              color: "#ffffff",
              fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
              fontSize: "12px",
            },
            formatter: function () {
              const p = this.point;
              const hours = p.hours !== undefined ? p.hours : p.value;
              const subtopicsStr =
                p.totalSubtopics > 0
                  ? `<div>• Subtopics: <b>${p.completedSubtopics || 0}/${p.totalSubtopics}</b> (${p.completionPercent || 0}%)</div>`
                  : "";
              const clickHint = p.isSubjectLevel
                ? `<div style="font-size:11px; color:#38bdf8; margin-top:5px; font-weight:600;">Click to drill down into topics &rarr;</div>`
                : "";
              return `
                <div style="font-weight:700; color:#38bdf8; font-size:13px; margin-bottom:4px;">${p.name}</div>
                <div style="font-size:11.5px; color:#cbd5e1; line-height:1.5;">
                  <div>• Completed Study: <b>${hours} hrs</b></div>
                  ${subtopicsStr}
                </div>
                ${clickHint}
              `;
            },
          },
          plotOptions: {
            treemap: {
              layoutAlgorithm: "squarified",
              alternateStartingDirection: true,
              borderWidth: 2,
              borderColor: "#ffffff",
              borderRadius: 6,
              cursor: "pointer",
              point: {
                events: {
                  click: function () {
                    const p = this;
                    if (p.isSubjectLevel) {
                      setSelectedHeatmapSubject(p.name);
                    }
                  },
                },
              },
              dataLabels: {
                enabled: true,
                align: "left",
                verticalAlign: "top",
                padding: 4,
                allowOverlap: true,
                crop: false,
                overflow: "justify",
                useHTML: true,
                style: {
                  fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                  fontSize: selectedHeatmapSubject ? "10px" : "12px",
                  fontWeight: "600",
                  color: "#ffffff",
                  textOutline: "none",
                  lineHeight: "1.2",
                  pointerEvents: "none",
                  width: "100%",
                },
                formatter: function () {
                  const p = this.point;
                  const hours = p.hours !== undefined ? p.hours : p.value;
                  const displayName = p.name || "";
                  const truncated = displayName.length > 28 ? `${displayName.slice(0, 26)}…` : displayName;
                  return `<div style="color:#ffffff; font-size:10px; font-weight:600; line-height:1.2; word-break:break-word; max-width:100%;">
                    <div style="font-weight:700; margin-bottom:2px;">${truncated}</div>
                    <div style="font-size:9px; opacity:0.85; font-weight:500;">${hours}h • ${p.completionPercent || 0}%</div>
                  </div>`;
                },
              },
            },
          },
          series: [
            {
              type: "treemap",
              data: treemapData,
            },
          ],
        });
      } catch (err) {
        console.error("Error creating Highcharts treemap on syllabus page:", err);
      }
    };

    if (window.Highcharts && window.Highcharts.seriesTypes && window.Highcharts.seriesTypes.treemap) {
      renderChart();
    } else {
      const loadScript = (src) => {
        return new Promise((resolve) => {
          if (document.querySelector(`script[src="${src}"]`)) {
            return resolve();
          }
          const s = document.createElement("script");
          s.src = src;
          s.async = true;
          s.onload = () => resolve();
          s.onerror = () => resolve();
          document.head.appendChild(s);
        });
      };

      loadScript("https://code.highcharts.com/highcharts.js")
        .then(() => loadScript("https://code.highcharts.com/modules/treemap.js"))
        .then(() => {
          if (isMounted) renderChart();
        });

      pollInterval = setInterval(() => {
        if (window.Highcharts && window.Highcharts.seriesTypes && window.Highcharts.seriesTypes.treemap) {
          clearInterval(pollInterval);
          if (isMounted) renderChart();
        }
      }, 150);
    }

    if (treemapContainerRef.current && window.ResizeObserver) {
      let animationFrameId = null;
      const ro = new ResizeObserver(() => {
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        animationFrameId = window.requestAnimationFrame(() => {
          if (chartInstanceRef.current) {
            try {
              chartInstanceRef.current.reflow();
            } catch (_) {}
          }
        });
      });
      ro.observe(treemapContainerRef.current);
      return () => {
        isMounted = false;
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        clearInterval(pollInterval);
        ro.disconnect();
        if (chartInstanceRef.current) {
          try {
            chartInstanceRef.current.destroy();
          } catch (_) {}
        }
      };
    }

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy();
        } catch (_) {}
      }
    };
  }, [treemapData, selectedHeatmapSubject]);

  return (
    <div className="syllabus-heatmap-wrapper">
      {/* Heatmap Subheader Strip */}
      <div className="syllabus-heatmap-header">
        <div className="syllabus-heatmap-breadcrumbs">
          <span
            className={`heatmap-crumb-root ${selectedHeatmapSubject ? "clickable" : "active"}`}
            onClick={() => setSelectedHeatmapSubject(null)}
          >
            All Subjects
          </span>
          {selectedHeatmapSubject && (
            <>
              <ChevronRight size={14} className="heatmap-crumb-sep" />
              <span className="heatmap-crumb-current">{selectedHeatmapSubject}</span>
            </>
          )}
        </div>

        <div className="syllabus-heatmap-meta-actions">
          {selectedHeatmapSubject && (
            <button
              type="button"
              className="btn-heatmap-back"
              onClick={() => setSelectedHeatmapSubject(null)}
            >
              &larr; Back to Subjects
            </button>
          )}
          <span className="syllabus-heatmap-pill hours">
            <strong>{heatmapSummary.hours}h</strong> logged
          </span>
          <span className="syllabus-heatmap-pill sessions">
            <strong>{heatmapSummary.sessions}</strong> sessions
          </span>
        </div>
      </div>

      {/* Main Heatmap Chart Area */}
      <div className="syllabus-heatmap-canvas-card">
        {loading ? (
          <div className="syllabus-heatmap-loading">
            <div className="loader"></div>
            <span>Loading syllabus heatmap...</span>
          </div>
        ) : treemapData.length === 0 ? (
          <div className="syllabus-heatmap-empty">
            <Activity size={32} className="syllabus-heatmap-empty-icon" />
            <h4>No Study Contributions Recorded</h4>
            <p>
              Your subject and topic distribution will appear here as you read articles and complete practice tests.
            </p>
          </div>
        ) : (
          <div
            ref={treemapContainerRef}
            className="syllabus-heatmap-chart"
            style={{ width: "100%", height: selectedHeatmapSubject ? "380px" : "340px" }}
          />
        )}
      </div>

      {/* Completion Legend Bar */}
      <div className="syllabus-heatmap-legend">
        <span className="legend-label">Completion:</span>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-swatch" style={{ background: BASE_TREEMAP_PALETTE[0] }} />
            <span>&ge; 70% Mastered</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch" style={{ background: BASE_TREEMAP_PALETTE[1] }} />
            <span>50-69% In Progress</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch" style={{ background: BASE_TREEMAP_PALETTE[2] }} />
            <span>30-49% Developing</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch" style={{ background: BASE_TREEMAP_PALETTE[3] }} />
            <span>10-29% Emerging</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch" style={{ background: BASE_TREEMAP_PALETTE[4] }} />
            <span>&lt; 10% Not Started</span>
          </div>
        </div>
      </div>
    </div>
  );
}
